import React, { useState, useEffect, useRef } from 'react';
import BADGE_ICON_MAP from '../constants/badge-icons';

const BADGE_COLORS = {
  speech_bubble: { bg: 'bg-blue-100', text: 'text-blue-600', ring: 'ring-blue-200', bar: 'bg-blue-500', stroke: '#3b82f6' },
  lightbulb: { bg: 'bg-amber-100', text: 'text-amber-600', ring: 'ring-amber-200', bar: 'bg-amber-500', stroke: '#f59e0b' },
  fire_3: { bg: 'bg-orange-100', text: 'text-orange-600', ring: 'ring-orange-200', bar: 'bg-orange-500', stroke: '#f97316' },
  fire_7: { bg: 'bg-red-100', text: 'text-red-600', ring: 'ring-red-200', bar: 'bg-red-500', stroke: '#ef4444' },
  compass: { bg: 'bg-teal-100', text: 'text-teal-600', ring: 'ring-teal-200', bar: 'bg-teal-500', stroke: '#14b8a6' },
  magnifying_glass: { bg: 'bg-indigo-100', text: 'text-indigo-600', ring: 'ring-indigo-200', bar: 'bg-indigo-500', stroke: '#6366f1' },
  camera: { bg: 'bg-purple-100', text: 'text-purple-600', ring: 'ring-purple-200', bar: 'bg-purple-500', stroke: '#a855f7' },
  star: { bg: 'bg-yellow-100', text: 'text-yellow-600', ring: 'ring-yellow-200', bar: 'bg-yellow-500', stroke: '#eab308' },
};

const DEFAULT_COLOR = { bg: 'bg-gray-100', text: 'text-gray-500', ring: 'ring-gray-200', bar: 'bg-gray-400', stroke: '#9ca3af' };

const parseProgress = (progress) => {
  if (!progress) return 0;
  const parts = progress.split('/');
  if (parts.length !== 2) return 0;
  const current = parseInt(parts[0], 10);
  const total = parseInt(parts[1], 10);
  if (total === 0) return 0;
  return Math.min((current / total) * 100, 100);
};

const ProgressRing = ({ percent, strokeColor, size = 56, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width={size} height={size} className="absolute inset-0 -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-700"
      />
    </svg>
  );
};

const AchievementBadges = ({ badges, title }) => {
  const [tooltip, setTooltip] = useState(null);
  const [newlyEarned, setNewlyEarned] = useState(new Set());
  const prevBadgesRef = useRef(null);

  // Detect newly earned badges by comparing with previous render
  useEffect(() => {
    if (prevBadgesRef.current && badges) {
      const prevMap = {};
      prevBadgesRef.current.forEach(b => { prevMap[b.id] = b.earned; });

      const earned = new Set();
      badges.forEach(b => {
        if (b.earned && prevMap[b.id] === false) {
          earned.add(b.id);
        }
      });

      if (earned.size > 0) {
        setNewlyEarned(earned);
        const timer = setTimeout(() => setNewlyEarned(new Set()), 2000);
        return () => clearTimeout(timer);
      }
    }
    prevBadgesRef.current = badges;
  }, [badges]);

  if (!badges || badges.length === 0) return null;

  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-900">{title || 'Achievements'}</h3>
        <div className="flex items-center gap-3">
          <div className="w-28 h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
              style={{ width: `${(earnedCount / badges.length) * 100}%` }}
            />
          </div>
          <span className="text-sm text-gray-500">{earnedCount}/{badges.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {badges.map((badge) => {
          const colors = BADGE_COLORS[badge.icon] || DEFAULT_COLOR;
          const IconComponent = BADGE_ICON_MAP[badge.icon];
          const isEarned = badge.earned;
          const isNew = newlyEarned.has(badge.id);
          const progressPct = parseProgress(badge.progress);

          return (
            <div
              key={badge.id}
              className="relative flex flex-col items-center cursor-pointer outline-none"
              tabIndex={0}
              role="button"
              aria-label={`${badge.name}: ${isEarned ? 'Earned' : 'Not earned'}. ${badge.description}. Progress: ${badge.progress}`}
              onMouseEnter={() => setTooltip(badge.id)}
              onMouseLeave={() => setTooltip(null)}
              onFocus={() => setTooltip(badge.id)}
              onBlur={() => setTooltip(null)}
              onClick={() => setTooltip(tooltip === badge.id ? null : badge.id)}
            >
              <div className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isEarned
                  ? `${colors.bg} ${colors.text} ring-2 ${colors.ring} shadow-md`
                  : 'bg-gray-100 text-gray-300 opacity-60'
              } ${isNew ? `animate-pulse-soft ring-4 ${colors.ring}` : ''}`}>
                {/* Progress ring for unearned badges */}
                {!isEarned && (
                  <ProgressRing percent={progressPct} strokeColor={colors.stroke} />
                )}
                {IconComponent ? (
                  <IconComponent size={28} />
                ) : (
                  <span className="text-2xl">?</span>
                )}
              </div>

              <span className={`text-xs mt-1.5 text-center font-medium leading-tight ${
                isEarned ? 'text-gray-800' : 'text-gray-400'
              }`}>
                {badge.name}
              </span>

              {tooltip === badge.id && (
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap z-20 shadow-lg">
                  <p className="font-semibold">{badge.name}</p>
                  <p className="text-gray-300">{badge.description}</p>
                  <p className="mt-1 text-indigo-300">{badge.progress}</p>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AchievementBadges;
